/**
 * Life Is Golden — "Build your set" bundle / matching builder.
 *
 * A premium, mobile-first campaign-landing surface (paid-social traffic): a pool
 * of products shown as tiles, an inline detail DRAWER (the centerpiece) with
 * zoomable imagery and button-based size selection, and a "Your set" panel that
 * tracks the chosen items, shows a struck-through subtotal vs. the set price, a
 * live "You're saving $X (Y%)" line, a tier hint, and one "Add set to cart" CTA.
 *
 * Cart pipeline is the proven Horizon path, copied verbatim from
 * assets/lig-pair-feature.js: explicit JSON POST to Theme.routes.cart_add_url
 * (ONE call, all set items) -> refetch /cart.js for the full cart object ->
 * dispatch CartAddEvent on `document` with the cart-items-component section ids,
 * so the pull-out cart morphs and opens. No reload, no navigation.
 *
 * Tier savings are a CLIENT-SIDE PREVIEW only: the real money comes off via a
 * native automatic discount at checkout. The preview percentage equals the
 * configured tier so there is no surprise at checkout.
 *
 * Imports use the theme's import-map aliases (@theme/*).
 */

import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Per-category size charts + fit reputation, keyed off product.type. Numbers are
 * the blanks' published garment measurements (laid flat, inches). `pos` is the
 * fit-marker position on a Runs-small(0) -> True(50) -> Runs-large(100) scale.
 */
const LIG_BB_SIZE = {
  adult: {
    label: 'Adult unisex tee',
    blank: 'Bella+Canvas 3001',
    fit: { tag: 'Fitted', note: 'Modern retail cut — runs a touch trim. Prefer a relaxed look? Size up.', pos: 38 },
    rows: [['S', '36', '28'], ['M', '40', '29'], ['L', '44', '30'], ['XL', '48', '31'], ['2XL', '52', '32'], ['3XL', '56', '33']],
  },
  kids: {
    label: 'Youth tee',
    blank: 'Bella+Canvas 3001Y',
    fit: { tag: 'True to size', note: 'True to standard US youth sizing.', pos: 50 },
    rows: [['S (6–8)', '28', '21.5'], ['M (10–12)', '30', '23'], ['L (14–16)', '32', '24.5'], ['XL (18–20)', '34', '26']],
  },
  toddler: {
    label: 'Toddler tee',
    blank: 'Rabbit Skins 3321',
    fit: { tag: 'True to size', note: 'True to US toddler age sizing (2T–5/6).', pos: 50 },
    rows: [['2 (2T)', '21', '15.5'], ['3 (3T)', '22', '16.5'], ['4 (4T)', '23', '17.5'], ['5/6', '24', '18.5']],
  },
  womens: {
    label: "Women's v-neck",
    blank: 'Bella+Canvas 6405',
    fit: { tag: 'Relaxed', note: 'Relaxed cut with a slightly longer body. True to size.', pos: 60 },
    rows: [['S', '33', '26.5'], ['M', '35', '27'], ['L', '38.5', '27.5'], ['XL', '42', '28'], ['2XL', '45.5', '28.5']],
  },
};
const LIG_BB_CAT_LABEL = { adult: 'Adult', kids: 'Kids', toddler: 'Toddler', womens: "Women's" };
function ligBbCategory(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('youth')) return 'kids';
  if (t.includes('toddler')) return 'toddler';
  if (t.includes('women')) return 'womens';
  return 'adult';
}

class LigBundleBuilder extends HTMLElement {
  /** @type {Array<any>} */
  #products = [];
  /** @type {Map<number, any>} pool product id -> product record */
  #byId = new Map();
  /** @type {Array<{min:number,pct:number}>} ascending by min */
  #tiers = [];
  /** in-memory set: product id -> chosen variant id */
  #set = new Map();
  /** per-product currently-selected variant id (persists across drawer opens) */
  #selectedVariant = new Map();
  #mode = 'outfit';
  #maxItems = 6;
  /** product id whose detail is open in the drawer, or null */
  #openProductId = null;
  #zoomed = false;
  /** cents -> formatted money fn, set in connectedCallback */
  #money;
  /** @type {{ scale:number, startDist:number }} */
  #pinch = { scale: 1, startDist: 0 };
  /** ordered slide records for the open product's gallery */
  #gallerySlides = [];
  #galleryIndex = 0;
  #galleryRaf = 0;
  #galleryAnimating = false;
  /** color option value (normalized) -> swatch hex */
  #colorMap = {
    navy: '#1f2a44',
    heathermidnightnavy: '#2a3040',
    heathernatural: '#e7e0cf',
    babyblue: '#bcd9ea',
    solidwhiteblend: '#f4f4f1',
    white: '#f6f6f4',
    athleticheather: '#cdd1d6',
    natural: '#ece6d6',
    softpink: '#f1d2d7',
    oceanblue: '#3f78a6',
    turquoise: '#53c4bf',
  };

  connectedCallback() {
    this.#mode = this.dataset.mode === 'matching' ? 'matching' : 'outfit';
    this.#maxItems = Math.max(2, Number(this.dataset.maxItems) || 6);
    this.#products = this.#parseProducts();

    if (!this.#products.length) {
      // Empty pool: render nothing.
      this.setAttribute('hidden', '');
      return;
    }

    for (const p of this.#products) {
      if (p && p.id != null) this.#byId.set(Number(p.id), p);
    }
    this.#tiers = this.#parseTiers();

    // Seed a sensible default variant per product (first available, else first).
    for (const p of this.#products) {
      const v = this.#defaultVariant(p);
      if (v) this.#selectedVariant.set(Number(p.id), Number(v.id));
    }

    // Cache region nodes.
    this.elTiles = this.querySelector('[data-bb-tiles]');
    this.elSetList = this.querySelector('[data-bb-set]');
    this.elSetEmpty = this.querySelector('[data-bb-set-empty]');
    this.elSubtotal = this.querySelector('[data-bb-subtotal]');
    this.elTotal = this.querySelector('[data-bb-total]');
    this.elSavings = this.querySelector('[data-bb-savings]');
    this.elSavingsRow = this.querySelector('[data-bb-savings-row]');
    this.elHint = this.querySelector('[data-bb-hint]');
    this.elCheckout = this.querySelector('[data-bb-checkout]');

    this.elDrawer = this.querySelector('[data-bb-drawer]');
    this.elScrim = this.querySelector('[data-bb-scrim]');
    this.elDrawerMedia = this.querySelector('[data-bb-drawer-media]');
    this.elDrawerTitle = this.querySelector('[data-bb-drawer-title]');
    this.elDrawerPrice = this.querySelector('[data-bb-drawer-price]');
    this.elDrawerBlurb = this.querySelector('[data-bb-drawer-blurb]');
    this.elDrawerSizes = this.querySelector('[data-bb-drawer-sizes]');
    this.elDrawerAdd = this.querySelector('[data-bb-drawer-add]');

    this.#money = this.#moneyFormatter();

    // Event wiring (delegated where possible).
    this.addEventListener('click', this.#handleClick);
    this.elGalleryTrack = this.querySelector('[data-bb-gallery-track]');
    if (this.elGalleryTrack instanceof HTMLElement) {
      this.elGalleryTrack.addEventListener('scroll', this.#onGalleryScroll, { passive: true });
    }
    document.addEventListener('keydown', this.#onKeydown);

    this.#renderSet();
    this.setAttribute('data-rendered', 'true');
    this.removeAttribute('hidden');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener('keydown', this.#onKeydown);
    if (this.elGalleryTrack instanceof HTMLElement) {
      this.elGalleryTrack.removeEventListener('scroll', this.#onGalleryScroll);
    }
  }

  /* --- Parsing --------------------------------------------------------- */

  #parseProducts() {
    try {
      const node = this.querySelector('[data-bb-products]');
      const data = node ? JSON.parse(node.textContent) : [];
      return Array.isArray(data) ? data.filter((p) => p && p.id != null) : [];
    } catch (error) {
      return [];
    }
  }

  #parseTiers() {
    let raw = [];
    try {
      raw = JSON.parse(this.dataset.tiers || '[]');
    } catch (error) {
      raw = [];
    }
    if (!Array.isArray(raw)) raw = [];
    return raw
      .map((t) => ({ min: Number(t && t.min), pct: Number(t && t.pct) }))
      .filter((t) => Number.isFinite(t.min) && Number.isFinite(t.pct) && t.min > 0 && t.pct > 0)
      .sort((a, b) => a.min - b.min);
  }

  /* --- Variant resolution ---------------------------------------------- */

  /**
   * First available variant, else the first variant, else null.
   * @param {any} product
   */
  #defaultVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return null;
    return variants.find((v) => v && v.available) || variants[0];
  }

  /**
   * The currently-selected variant record for a product (resolves the stored
   * variant id back to the full record so we can read its price/availability).
   * @param {any} product
   */
  #currentVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return null;
    const chosenId = this.#selectedVariant.get(Number(product.id));
    return variants.find((v) => Number(v.id) === Number(chosenId)) || this.#defaultVariant(product);
  }

  /**
   * Given a product and a clicked size value, find the variant whose option
   * matching the size dimension equals that value, preferring an available one.
   * Sizes are taken from whichever option name looks like "Size" (else the last
   * option). Other selected option values are held from the current variant.
   * @param {any} product
   * @param {number} optionIndex 0-based option position used as the "size" axis
   * @param {string} value
   */
  #resolveBySize(product, optionIndex, value) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return null;
    const key = `option${optionIndex + 1}`;
    const current = this.#currentVariant(product);
    // Hold the OTHER options fixed to the current variant's values.
    const match = (v, exact) => {
      if (String(v[key]) !== String(value)) return false;
      for (let i = 1; i <= 3; i += 1) {
        if (i - 1 === optionIndex) continue;
        const k = `option${i}`;
        if (current && current[k] != null && exact && String(v[k]) !== String(current[k])) {
          return false;
        }
      }
      return true;
    };
    // Prefer an exact other-option match that is available, then exact, then any
    // variant with this size value that is available, then any with this value.
    return (
      variants.find((v) => match(v, true) && v.available) ||
      variants.find((v) => match(v, true)) ||
      variants.find((v) => String(v[key]) === String(value) && v.available) ||
      variants.find((v) => String(v[key]) === String(value)) ||
      null
    );
  }

  /** Index of the option to treat as the size axis (named "Size", else last). */
  #sizeOptionIndex(product) {
    const options = Array.isArray(product?.options) ? product.options : [];
    if (!options.length) return 0;
    const idx = options.findIndex((o) => o && /size/i.test(String(o.name)));
    return idx >= 0 ? idx : options.length - 1;
  }

  /* --- Click delegation ------------------------------------------------ */

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Size guide open / close (checked before the drawer scrim so the modal wins).
    if (target.closest('[data-bb-sizeguide-close]')) {
      event.preventDefault();
      this.#closeSizeGuide();
      return;
    }
    if (target.closest('[data-bb-size-guide]')) {
      event.preventDefault();
      this.#openSizeGuide();
      return;
    }

    // Scrim / close -> close drawer.
    if (target.closest('[data-bb-scrim]') || target.closest('[data-bb-drawer-close]')) {
      event.preventDefault();
      this.#closeDrawer();
      return;
    }

    // Tile -> open drawer for that product.
    const tile = target.closest('[data-bb-tile]');
    if (tile instanceof HTMLElement) {
      event.preventDefault();
      this.#openDrawer(Number(tile.dataset.id));
      return;
    }

    // Drawer color swatch.
    const colorBtn = target.closest('[data-bb-color]');
    if (colorBtn instanceof HTMLButtonElement && !colorBtn.disabled) {
      event.preventDefault();
      this.#chooseColor(colorBtn);
      return;
    }

    // Gallery arrows.
    if (target.closest('[data-bb-gallery-prev]')) {
      event.preventDefault();
      this.#galleryStep(-1);
      return;
    }
    if (target.closest('[data-bb-gallery-next]')) {
      event.preventDefault();
      this.#galleryStep(1);
      return;
    }

    // Gallery thumbnail.
    const thumb = target.closest('[data-bb-thumb]');
    if (thumb instanceof HTMLElement) {
      event.preventDefault();
      this.#galleryGoTo(Number(thumb.dataset.index) || 0);
      return;
    }

    // Gallery slide -> toggle zoom.
    if (target.closest('[data-bb-slide]')) {
      event.preventDefault();
      this.#toggleZoom();
      return;
    }

    // Drawer size button.
    const sizeBtn = target.closest('[data-bb-size]');
    if (sizeBtn instanceof HTMLButtonElement && !sizeBtn.disabled) {
      event.preventDefault();
      this.#chooseSize(sizeBtn);
      return;
    }

    // Drawer "Add to set".
    if (target.closest('[data-bb-drawer-add]')) {
      event.preventDefault();
      this.#addOpenToSet();
      return;
    }

    // Set-panel: remove a line.
    const removeBtn = target.closest('[data-bb-remove]');
    if (removeBtn instanceof HTMLElement) {
      event.preventDefault();
      this.#removeFromSet(Number(removeBtn.dataset.id));
      return;
    }

    // Set-panel: matching nudge -> open the partner's drawer so the shopper
    // chooses color + size (rather than blindly adding the default variant).
    const matchBtn = target.closest('[data-bb-match-add]');
    if (matchBtn instanceof HTMLElement) {
      event.preventDefault();
      this.#openDrawer(Number(matchBtn.dataset.id));
      return;
    }

    // Preset chip -> fill the set with its ids.
    const preset = target.closest('[data-bb-preset]');
    if (preset instanceof HTMLElement) {
      event.preventDefault();
      this.#applyPreset(preset.dataset.ids || '');
      return;
    }

    // Checkout / Add set to cart.
    if (target.closest('[data-bb-checkout]')) {
      event.preventDefault();
      this.#addSetToCart();
      return;
    }
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const sizeGuide = this.querySelector('[data-bb-sizeguide]');
    if (sizeGuide instanceof HTMLElement && !sizeGuide.hidden) {
      this.#closeSizeGuide();
      return;
    }
    if (this.#openProductId != null) this.#closeDrawer();
  };

  /* --- Drawer ---------------------------------------------------------- */

  #openDrawer(productId) {
    const product = this.#byId.get(Number(productId));
    if (!product || !this.elDrawer) return;
    this.#openProductId = Number(productId);
    this.#zoomed = false;
    this.#pinch.scale = 1;

    if (this.elDrawerTitle) this.elDrawerTitle.textContent = product.title || '';
    if (this.elDrawerBlurb) {
      const blurb = this.#mode === 'matching' ? product.blurb || '' : product.blurb || '';
      this.elDrawerBlurb.textContent = blurb;
      this.elDrawerBlurb.hidden = !blurb;
    }

    // Category badge + fit indicator (per blank).
    this.#syncDrawerMeta(product);
    // Color swatches + media carousel.
    this.#renderColors(product);
    this.#renderGallery(product);
    // Sizes.
    this.#renderSizes(product);
    // Price reflects the currently-selected variant.
    this.#syncDrawerPrice(product);
    this.#syncDrawerAdd(product);

    this.elDrawer.classList.add('is-open');
    if (this.elScrim instanceof HTMLElement) this.elScrim.classList.add('is-open');
    this.elDrawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('lig-bb-locked');
    const focusTarget = this.querySelector('[data-bb-drawer-close]');
    if (focusTarget instanceof HTMLElement) focusTarget.focus();
  }

  #closeDrawer() {
    if (!this.elDrawer) return;
    this.#closeSizeGuide();
    this.elDrawer.classList.remove('is-open');
    if (this.elScrim instanceof HTMLElement) this.elScrim.classList.remove('is-open');
    this.elDrawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('lig-bb-locked');
    this.#zoomed = false;
    const reopenId = this.#openProductId;
    this.#openProductId = null;
    // Return focus to the originating tile if it still exists.
    if (reopenId != null) {
      const tile = this.querySelector(`[data-bb-tile][data-id="${reopenId}"]`);
      if (tile instanceof HTMLElement) tile.focus();
    }
  }

  /**
   * @param {any} product
   * @param {boolean} zoom
   */
  #norm(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  /** Index of the option named "Color", or -1. */
  #colorOptionIndex(product) {
    const options = Array.isArray(product?.options) ? product.options : [];
    return options.findIndex((o) => o && /colou?r/i.test(String(o.name)));
  }

  /** The currently-selected color value for a product (or null if no color axis). */
  #selectedColor(product) {
    const ci = this.#colorOptionIndex(product);
    if (ci < 0) return null;
    const v = this.#currentVariant(product);
    return v ? v[`option${ci + 1}`] : null;
  }

  /** Parse {color, front, lifestyle} out of an image's alt text ("Title — Navy (front)"). */
  #imageMeta(alt) {
    const text = String(alt || '');
    const front = /\(front\)/i.test(text);
    const lifestyle = /lifestyle/i.test(text) || text.trim() === '';
    let color = null;
    const parts = text.split('—'); // em dash
    if (parts.length > 1) {
      color = parts[1].replace(/\((?:front|back)\)/i, '').trim();
      if (/lifestyle/i.test(color) || !color) color = null;
    }
    return { color, front, lifestyle };
  }

  /** Ordered slide list for the open product, biased to the selected color, lifestyle last. */
  #gallerySlidesFor(product) {
    const imgs =
      Array.isArray(product.images) && product.images.length
        ? product.images
        : product.image
          ? [{ url: product.image, alt: product.title }]
          : [];
    const sel = this.#norm(this.#selectedColor(product));
    const meta = imgs.map((im) => ({ url: im.url, alt: im.alt, ...this.#imageMeta(im.alt) }));
    let colorMatched = sel ? meta.filter((m) => m.color && this.#norm(m.color) === sel) : [];
    const lifestyle = meta.filter((m) => m.lifestyle || !m.color);
    if (colorMatched.length === 0) {
      colorMatched = meta.filter((m) => m.color);
      if (colorMatched.length === 0) colorMatched = meta.filter((m) => !m.lifestyle);
    }
    colorMatched.sort((a, b) => (b.front ? 1 : 0) - (a.front ? 1 : 0));
    const ordered = [...colorMatched, ...lifestyle];
    const seen = new Set();
    const out = [];
    for (const m of ordered) {
      if (seen.has(m.url)) continue;
      seen.add(m.url);
      out.push(m);
    }
    return out.length ? out : meta;
  }

  /** Build the carousel slides + thumbnails for the open product/color. */
  #renderGallery(product) {
    const track = this.querySelector('[data-bb-gallery-track]');
    const thumbs = this.querySelector('[data-bb-thumbs]');
    if (!(track instanceof HTMLElement)) return;

    const slides = this.#gallerySlidesFor(product);
    this.#gallerySlides = slides;
    this.#galleryIndex = 0;
    this.#zoomed = false;
    const gallery = this.querySelector('[data-bb-gallery]');
    if (gallery) gallery.classList.remove('is-zoomed');

    const tf = document.createDocumentFragment();
    slides.forEach((s, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lig-bb__slide';
      btn.setAttribute('data-bb-slide', '');
      btn.dataset.index = String(i);
      const img = document.createElement('img');
      img.className = 'lig-bb__slide-img';
      img.src = this.#withWidth(s.url, 1100);
      img.alt = s.alt || product.title || '';
      img.loading = i === 0 ? 'eager' : 'lazy';
      img.draggable = false;
      btn.appendChild(img);
      tf.appendChild(btn);
    });
    track.replaceChildren(tf);
    track.scrollTo({ left: 0, behavior: 'instant' });

    if (thumbs instanceof HTMLElement) {
      if (slides.length > 1) {
        thumbs.hidden = false;
        const thf = document.createDocumentFragment();
        slides.forEach((s, i) => {
          const t = document.createElement('button');
          t.type = 'button';
          t.className = 'lig-bb__thumb' + (i === 0 ? ' is-active' : '');
          t.setAttribute('data-bb-thumb', '');
          t.dataset.index = String(i);
          const ti = document.createElement('img');
          ti.src = this.#withWidth(s.url, 200);
          ti.alt = '';
          ti.loading = 'lazy';
          t.appendChild(ti);
          thf.appendChild(t);
        });
        thumbs.replaceChildren(thf);
      } else {
        thumbs.hidden = true;
        thumbs.replaceChildren();
      }
    }
    this.#updateGalleryUi();
  }

  #updateGalleryUi() {
    const n = this.#gallerySlides.length;
    const i = this.#galleryIndex;
    const counter = this.querySelector('[data-bb-gallery-counter]');
    if (counter instanceof HTMLElement) {
      counter.textContent = n > 1 ? `${i + 1} / ${n}` : '';
      counter.hidden = n <= 1;
    }
    const prev = this.querySelector('[data-bb-gallery-prev]');
    const next = this.querySelector('[data-bb-gallery-next]');
    if (prev instanceof HTMLElement) prev.hidden = n <= 1;
    if (next instanceof HTMLElement) next.hidden = n <= 1;
    this.querySelectorAll('[data-bb-thumb]').forEach((t) => {
      t.classList.toggle('is-active', Number(t.dataset.index) === i);
    });
  }

  #onGalleryScroll = () => {
    if (this.#galleryAnimating) return;
    const track = this.elGalleryTrack;
    if (!(track instanceof HTMLElement)) return;
    const w = track.clientWidth || 1;
    const i = Math.max(0, Math.min(Math.round(track.scrollLeft / w), this.#gallerySlides.length - 1));
    if (i !== this.#galleryIndex) {
      this.#galleryIndex = i;
      if (this.#zoomed) {
        this.#zoomed = false;
        const g = this.querySelector('[data-bb-gallery]');
        if (g) g.classList.remove('is-zoomed');
      }
      this.#updateGalleryUi();
    }
  };

  #galleryStep(dir) {
    const target = Math.max(0, Math.min(this.#galleryIndex + dir, this.#gallerySlides.length - 1));
    this.#galleryScrollTo(target);
  }

  #galleryGoTo(i) {
    const target = Math.max(0, Math.min(i, this.#gallerySlides.length - 1));
    this.#galleryScrollTo(target);
  }

  /**
   * Programmatically scroll to a slide. Native `scrollTo({behavior:'smooth'})`
   * is a no-op on this snap container (browser quirk), so we animate scrollLeft
   * by hand with rAF (per-frame assignment IS honored) with snap disabled during
   * the run. The index/UI update optimistically; the scroll listener is paused
   * so intermediate frames don't fight the counter.
   */
  #galleryScrollTo(target) {
    const track = this.elGalleryTrack;
    if (!(track instanceof HTMLElement)) return;
    this.#galleryIndex = target;
    this.#updateGalleryUi();
    const to = target * track.clientWidth;
    const from = track.scrollLeft;
    cancelAnimationFrame(this.#galleryRaf);
    if (Math.abs(to - from) < 2) return;
    this.#galleryAnimating = true;
    track.style.scrollSnapType = 'none';
    const start = performance.now();
    const dur = 300;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur);
      track.scrollLeft = from + (to - from) * ease(t);
      if (t < 1) {
        this.#galleryRaf = requestAnimationFrame(step);
      } else {
        track.style.scrollSnapType = '';
        this.#galleryAnimating = false;
      }
    };
    this.#galleryRaf = requestAnimationFrame(step);
  }

  /** Toggle zoom on the active slide image (swaps in a higher-res source). */
  #toggleZoom = () => {
    const gallery = this.querySelector('[data-bb-gallery]');
    if (!(gallery instanceof HTMLElement)) return;
    this.#zoomed = !this.#zoomed;
    gallery.classList.toggle('is-zoomed', this.#zoomed);
    const slide = this.#gallerySlides[this.#galleryIndex];
    const imgs = this.querySelectorAll('[data-bb-slide] img');
    const active = imgs[this.#galleryIndex];
    if (active instanceof HTMLImageElement && slide) {
      active.src = this.#withWidth(slide.url, this.#zoomed ? 2000 : 1100);
    }
  };

  /** Render color choices as swatches; updates the "Color: X" label. */
  #renderColors(product) {
    const host = this.querySelector('[data-bb-drawer-colors]');
    const head = this.querySelector('[data-bb-color-head]');
    const valueEl = head ? head.querySelector('[data-bb-color-value]') : null;
    if (!(host instanceof HTMLElement)) return;
    const ci = this.#colorOptionIndex(product);
    if (ci < 0) {
      host.hidden = true;
      host.replaceChildren();
      if (head instanceof HTMLElement) head.hidden = true;
      return;
    }
    const option = product.options[ci];
    const values = Array.isArray(option?.values) ? option.values : [];
    const key = `option${ci + 1}`;
    const current = this.#currentVariant(product);
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (head instanceof HTMLElement) head.hidden = false;
    if (valueEl) valueEl.textContent = current ? String(current[key] || '') : String(values[0] || '');

    if (values.length <= 1) {
      host.hidden = true;
      host.replaceChildren();
      return;
    }
    host.hidden = false;
    const frag = document.createDocumentFragment();
    for (const val of values) {
      const available = variants.some((v) => String(v[key]) === String(val) && v.available);
      const selected = current && String(current[key]) === String(val);
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'lig-bb__swatch' + (selected ? ' is-selected' : '') + (available ? '' : ' is-unavailable');
      b.setAttribute('data-bb-color', '');
      b.dataset.optionIndex = String(ci);
      b.dataset.value = String(val);
      b.title = String(val);
      b.setAttribute('aria-label', String(val));
      b.setAttribute('aria-pressed', selected ? 'true' : 'false');
      b.style.setProperty('--lig-bb-swatch', this.#colorMap[this.#norm(val)] || '#c9c9c9');
      if (!available) {
        b.disabled = true;
        b.setAttribute('aria-disabled', 'true');
      }
      frag.appendChild(b);
    }
    host.replaceChildren(frag);
  }

  /** @param {HTMLButtonElement} button */
  #chooseColor(button) {
    if (this.#openProductId == null) return;
    const product = this.#byId.get(this.#openProductId);
    if (!product) return;
    const optionIndex = Number(button.dataset.optionIndex) || 0;
    const value = button.dataset.value || '';
    const variant = this.#resolveBySize(product, optionIndex, value);
    if (!variant) return;
    this.#selectedVariant.set(Number(product.id), Number(variant.id));
    this.#renderColors(product);
    this.#renderSizes(product);
    this.#syncDrawerPrice(product);
    this.#syncDrawerAdd(product);
    this.#renderGallery(product);
  }

  /** Set the drawer's category badge + fit indicator from the product's blank. */
  #syncDrawerMeta(product) {
    const cat = ligBbCategory(product.type);
    const data = LIG_BB_SIZE[cat] || LIG_BB_SIZE.adult;
    const badge = this.querySelector('[data-bb-drawer-cat]');
    if (badge instanceof HTMLElement) {
      badge.textContent = LIG_BB_CAT_LABEL[cat];
      badge.className = 'lig-bb__cat lig-bb__drawer-cat lig-bb__cat--' + cat;
      badge.hidden = false;
    }
    const tag = this.querySelector('[data-bb-fit-tag]');
    if (tag instanceof HTMLElement) tag.textContent = data.fit.tag;
    const marker = this.querySelector('[data-bb-fit-marker]');
    if (marker instanceof HTMLElement) marker.style.insetInlineStart = data.fit.pos + '%';
    const note = this.querySelector('[data-bb-fit-note]');
    if (note instanceof HTMLElement) note.textContent = data.fit.note;
  }

  #openSizeGuide() {
    if (this.#openProductId == null) return;
    const product = this.#byId.get(this.#openProductId);
    if (!product) return;
    const cat = ligBbCategory(product.type);
    const data = LIG_BB_SIZE[cat] || LIG_BB_SIZE.adult;
    const modal = this.querySelector('[data-bb-sizeguide]');
    if (!(modal instanceof HTMLElement)) return;

    const title = this.querySelector('[data-bb-sizeguide-title]');
    const sub = this.querySelector('[data-bb-sizeguide-sub]');
    const body = this.querySelector('[data-bb-sizeguide-body]');
    if (title instanceof HTMLElement) title.textContent = data.label + ' — size guide';
    if (sub instanceof HTMLElement) sub.textContent = `${data.blank} · ${data.fit.tag}. ${data.fit.note}`;
    if (body instanceof HTMLElement) {
      const table = document.createElement('table');
      table.className = 'lig-bb__sizetable';
      const thead = document.createElement('thead');
      const htr = document.createElement('tr');
      for (const c of ['Size', 'Chest', 'Length']) {
        const th = document.createElement('th');
        th.textContent = c;
        htr.appendChild(th);
      }
      thead.appendChild(htr);
      table.appendChild(thead);
      const tbody = document.createElement('tbody');
      for (const row of data.rows) {
        const tr = document.createElement('tr');
        row.forEach((cell, i) => {
          const td = document.createElement(i === 0 ? 'th' : 'td');
          td.textContent = cell + (i === 0 ? '' : '"');
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);
      body.replaceChildren(table);
    }
    modal.hidden = false;
    requestAnimationFrame(() => modal.classList.add('is-open'));
  }

  #closeSizeGuide() {
    const modal = this.querySelector('[data-bb-sizeguide]');
    if (!(modal instanceof HTMLElement)) return;
    modal.classList.remove('is-open');
    modal.hidden = true;
  }

  /** Render size selection as BUTTONS (never a dropdown). */
  #renderSizes(product) {
    if (!(this.elDrawerSizes instanceof HTMLElement)) return;
    const options = Array.isArray(product.options) ? product.options : [];
    const variants = Array.isArray(product.variants) ? product.variants : [];

    if (!options.length || !variants.length) {
      this.elDrawerSizes.replaceChildren();
      this.elDrawerSizes.hidden = true;
      return;
    }
    this.elDrawerSizes.hidden = false;

    const sizeIdx = this.#sizeOptionIndex(product);
    const sizeOption = options[sizeIdx];
    const values = Array.isArray(sizeOption?.values) ? sizeOption.values : [];
    const key = `option${sizeIdx + 1}`;
    const current = this.#currentVariant(product);

    const fragment = document.createDocumentFragment();
    for (const value of values) {
      // A size value is available if ANY variant with that value is available.
      const available = variants.some((v) => String(v[key]) === String(value) && v.available);
      const isSelected = current && String(current[key]) === String(value);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lig-bb__size';
      btn.setAttribute('data-bb-size', '');
      btn.dataset.optionIndex = String(sizeIdx);
      btn.dataset.value = String(value);
      btn.textContent = String(value);
      if (!available) {
        btn.disabled = true;
        btn.classList.add('is-unavailable');
        btn.setAttribute('aria-disabled', 'true');
        btn.title = 'Sold out';
      }
      if (isSelected && available) {
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.setAttribute('aria-pressed', 'false');
      }
      fragment.appendChild(btn);
    }
    this.elDrawerSizes.replaceChildren(fragment);
  }

  /** @param {HTMLButtonElement} button */
  #chooseSize(button) {
    if (this.#openProductId == null) return;
    const product = this.#byId.get(this.#openProductId);
    if (!product) return;
    const optionIndex = Number(button.dataset.optionIndex) || 0;
    const value = button.dataset.value || '';
    const variant = this.#resolveBySize(product, optionIndex, value);
    if (!variant) return;
    this.#selectedVariant.set(Number(product.id), Number(variant.id));
    // Re-render sizes to move the selected state, and resync price/CTA.
    this.#renderSizes(product);
    this.#syncDrawerPrice(product);
    this.#syncDrawerAdd(product);
  }

  #syncDrawerPrice(product) {
    if (!(this.elDrawerPrice instanceof HTMLElement)) return;
    const variant = this.#currentVariant(product);
    const cents = variant ? variant.price : product.price;
    this.elDrawerPrice.textContent = this.#money(cents);
  }

  #syncDrawerAdd(product) {
    if (!(this.elDrawerAdd instanceof HTMLButtonElement)) return;
    const variant = this.#currentVariant(product);
    const inSet = this.#set.has(Number(product.id));
    const full = !inSet && this.#set.size >= this.#maxItems;
    const label = this.elDrawerAdd.querySelector('[data-bb-drawer-add-label]') || this.elDrawerAdd;

    if (!variant || variant.available === false) {
      this.elDrawerAdd.disabled = true;
      label.textContent = 'Sold out';
      return;
    }
    if (full) {
      this.elDrawerAdd.disabled = true;
      label.textContent = `Set is full (${this.#maxItems} max)`;
      return;
    }
    this.elDrawerAdd.disabled = false;
    label.textContent = inSet ? 'Update size in set' : 'Add to set';
  }

  #addOpenToSet() {
    if (this.#openProductId == null) return;
    const ok = this.#addToSet(this.#openProductId);
    if (ok) this.#closeDrawer();
  }

  /* --- Set state ------------------------------------------------------- */

  /**
   * Add a pool product (by id) to the set with its currently-selected variant.
   * @returns {boolean} true if added/updated, false if rejected
   */
  #addToSet(productId) {
    const product = this.#byId.get(Number(productId));
    if (!product) return false;
    const already = this.#set.has(Number(productId));
    if (!already && this.#set.size >= this.#maxItems) return false;
    const variant = this.#currentVariant(product);
    if (!variant || variant.available === false) return false;
    this.#set.set(Number(productId), Number(variant.id));
    this.#renderSet();
    return true;
  }

  #removeFromSet(productId) {
    this.#set.delete(Number(productId));
    this.#renderSet();
    // Keep the drawer CTA accurate if it is open for this product.
    if (this.#openProductId != null) {
      const open = this.#byId.get(this.#openProductId);
      if (open) this.#syncDrawerAdd(open);
    }
  }

  /** Fill the set from a comma-separated id string (preset chip). */
  #applyPreset(idsCsv) {
    const ids = String(idsCsv)
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!ids.length) return;
    this.#set.clear();
    for (const id of ids) {
      if (this.#set.size >= this.#maxItems) break;
      const product = this.#byId.get(id);
      if (!product) continue;
      const variant = this.#currentVariant(product);
      if (!variant || variant.available === false) continue;
      this.#set.set(id, Number(variant.id));
    }
    this.#renderSet();
  }

  /* --- Tier math (preview only) --------------------------------------- */

  /** Highest tier whose min <= current set size, or null. */
  #activeTier() {
    const count = this.#set.size;
    let active = null;
    for (const tier of this.#tiers) {
      if (tier.min <= count) active = tier;
    }
    return active;
  }

  /** Next tier above the current set size, or null if at/over the top tier. */
  #nextTier() {
    const count = this.#set.size;
    for (const tier of this.#tiers) {
      if (tier.min > count) return tier;
    }
    return null;
  }

  /* --- Rendering the set panel ---------------------------------------- */

  #renderSet() {
    const count = this.#set.size;

    // Line list. The empty-state message is the server-rendered
    // [data-bb-set-empty] sibling; we toggle it rather than injecting a <p>
    // into the <ul> (which would be invalid markup and never clear).
    if (this.elSetEmpty instanceof HTMLElement) {
      this.elSetEmpty.hidden = count > 0;
    }
    if (this.elSetList instanceof HTMLElement) {
      if (!count) {
        this.elSetList.replaceChildren();
      } else {
        const fragment = document.createDocumentFragment();
        for (const [productId, variantId] of this.#set) {
          const product = this.#byId.get(Number(productId));
          if (!product) continue;
          const variant = (Array.isArray(product.variants) ? product.variants : []).find(
            (v) => Number(v.id) === Number(variantId)
          );
          fragment.appendChild(this.#setLine(product, variant));
        }
        this.elSetList.replaceChildren(fragment);
      }
    }

    // Money preview.
    let subtotal = 0;
    for (const [productId, variantId] of this.#set) {
      const product = this.#byId.get(Number(productId));
      if (!product) continue;
      const variant = (Array.isArray(product.variants) ? product.variants : []).find(
        (v) => Number(v.id) === Number(variantId)
      );
      subtotal += Number(variant ? variant.price : product.price) || 0;
    }
    const tier = this.#activeTier();
    const pct = tier ? tier.pct : 0;
    const savingsCents = Math.round((subtotal * pct) / 100);
    const total = subtotal - savingsCents;

    if (this.elSubtotal instanceof HTMLElement) {
      this.elSubtotal.textContent = this.#money(subtotal);
      this.elSubtotal.hidden = pct === 0; // only strike through when a discount applies
    }
    if (this.elTotal instanceof HTMLElement) {
      this.elTotal.textContent = this.#money(total);
    }
    if (this.elSavings instanceof HTMLElement) {
      if (pct > 0) {
        this.elSavings.textContent = `You're saving ${this.#money(savingsCents)} (${pct}%)`;
      } else {
        this.elSavings.textContent = '';
      }
    }
    // Toggle the ROW wrapper — CSS hides it via [hidden], and it carries the
    // initial hidden attribute, so toggling the inner span alone never reveals it.
    if (this.elSavingsRow instanceof HTMLElement) {
      this.elSavingsRow.hidden = pct === 0;
    }

    // Tier hint.
    if (this.elHint instanceof HTMLElement) {
      const next = this.#nextTier();
      if (count === 0) {
        this.elHint.textContent =
          this.#tiers.length > 0
            ? `Add ${this.#tiers[0].min} pieces to save ${this.#tiers[0].pct}%.`
            : '';
        this.elHint.hidden = !this.elHint.textContent;
      } else if (next) {
        const needed = next.min - count;
        this.elHint.hidden = false;
        this.elHint.textContent = `Add ${needed} more to save ${next.pct}%.`;
      } else if (tier) {
        this.elHint.hidden = false;
        this.elHint.textContent = `Top tier unlocked — ${tier.pct}% off your set.`;
      } else {
        this.elHint.hidden = true;
        this.elHint.textContent = '';
      }
    }

    // Checkout button state.
    if (this.elCheckout instanceof HTMLButtonElement) {
      this.elCheckout.disabled = count === 0 || this.elCheckout.getAttribute('aria-busy') === 'true';
      const label = this.elCheckout.querySelector('[data-bb-checkout-label]') || this.elCheckout;
      if (this.elCheckout.getAttribute('aria-busy') !== 'true') {
        label.textContent = count === 0 ? 'Add set to cart' : `Add ${count} to cart — ${this.#money(total)}`;
      }
    }

    // Reflect count for sticky-bar CSS hooks.
    this.dataset.bbCount = String(count);

    // Matching nudge.
    this.#renderMatchNudge();
  }

  /**
   * @param {any} product
   * @param {any} variant
   * @returns {HTMLElement}
   */
  #setLine(product, variant) {
    const li = document.createElement('li');
    li.className = 'lig-bb__line';
    li.setAttribute('data-bb-set-line', '');
    li.dataset.id = String(product.id);

    if (product.image) {
      const media = document.createElement('span');
      media.className = 'lig-bb__line-media';
      const img = document.createElement('img');
      img.className = 'lig-bb__line-img';
      img.src = this.#withWidth(product.image, 160);
      img.alt = product.title || '';
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      media.appendChild(img);
      li.appendChild(media);
    }

    const info = document.createElement('div');
    info.className = 'lig-bb__line-info';

    const name = document.createElement('span');
    name.className = 'lig-bb__line-title';
    name.textContent = product.title || '';
    info.appendChild(name);

    // Variant sub-label (size/color) when not the default-only variant.
    const sub = this.#variantLabel(product, variant);
    if (sub) {
      const subEl = document.createElement('span');
      subEl.className = 'lig-bb__line-variant';
      subEl.textContent = sub;
      info.appendChild(subEl);
    }
    li.appendChild(info);

    const price = document.createElement('span');
    price.className = 'lig-bb__line-price';
    price.textContent = this.#money(variant ? variant.price : product.price);
    li.appendChild(price);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'lig-bb__line-remove';
    remove.setAttribute('data-bb-remove', '');
    remove.dataset.id = String(product.id);
    remove.setAttribute('aria-label', `Remove ${product.title || 'item'} from set`);
    remove.textContent = '×'; // ×
    li.appendChild(remove);

    return li;
  }

  /** Build a human option label from a variant, e.g. "Navy / L". */
  #variantLabel(product, variant) {
    if (!variant) return '';
    const options = Array.isArray(product.options) ? product.options : [];
    if (!options.length) return '';
    const parts = [];
    for (let i = 0; i < Math.min(3, options.length); i += 1) {
      const v = variant[`option${i + 1}`];
      if (v != null && String(v) !== 'Default Title') parts.push(String(v));
    }
    return parts.join(' / ');
  }

  /**
   * Matching-mode nudge: if an item in the set has a `match` id that is in the
   * pool and not yet in the set, surface a one-tap "add the matching size".
   */
  #renderMatchNudge() {
    const host = this.querySelector('[data-bb-match]');
    if (!(host instanceof HTMLElement)) return;

    if (this.#mode !== 'matching' || this.#set.size === 0 || this.#set.size >= this.#maxItems) {
      host.replaceChildren();
      host.hidden = true;
      return;
    }

    // Find the first set item whose match is available, in the pool, not in set.
    let matchProduct = null;
    for (const productId of this.#set.keys()) {
      const product = this.#byId.get(Number(productId));
      const matchId = product && product.match != null ? Number(product.match) : null;
      if (matchId == null) continue;
      if (this.#set.has(matchId)) continue;
      const candidate = this.#byId.get(matchId);
      if (!candidate) continue;
      const variant = this.#currentVariant(candidate);
      if (!variant || variant.available === false) continue;
      matchProduct = candidate;
      break;
    }

    if (!matchProduct) {
      host.replaceChildren();
      host.hidden = true;
      return;
    }

    host.hidden = false;
    const text = document.createElement('span');
    text.className = 'lig-bb__match-text';
    text.textContent = `Add the matching ${matchProduct.title}`;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lig-bb__match-add';
    btn.setAttribute('data-bb-match-add', '');
    btn.dataset.id = String(matchProduct.id);
    btn.textContent = 'Add';
    host.replaceChildren(text, btn);
  }

  /* --- Add set to cart (proven Horizon pipeline) ----------------------- */

  async #addSetToCart() {
    if (!(this.elCheckout instanceof HTMLButtonElement)) return;
    if (this.#set.size === 0) return;
    if (this.elCheckout.getAttribute('aria-busy') === 'true') return;

    this.elCheckout.setAttribute('aria-busy', 'true');
    this.elCheckout.disabled = true;

    const items = [];
    for (const variantId of this.#set.values()) {
      items.push({ id: Number(variantId) || variantId, quantity: 1 });
    }

    const sections = this.#cartSectionIds();
    const body = JSON.stringify({
      items,
      sections,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });
      const responseText = await response.text();

      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Unexpected cart response');
      }

      if (!response.ok || parsed.status) {
        document.dispatchEvent(
          new CartErrorEvent('lig-bundle-builder', parsed.message, parsed.description, parsed.errors)
        );
        this.#resetCheckout();
        return;
      }

      const cart = await this.#fetchCart();
      const itemCount =
        typeof parsed.item_count === 'number'
          ? parsed.item_count
          : typeof cart?.item_count === 'number'
            ? cart.item_count
            : undefined;

      document.dispatchEvent(
        new CartAddEvent(cart ?? parsed, 'lig-bundle-builder', {
          source: 'lig-bundle-builder',
          itemCount,
          setItemCount: items.length,
          sections: parsed.sections,
        })
      );

      // Slide open Horizon's cart drawer so the add is visibly confirmed.
      // CartAddEvent morphs the drawer contents but does not open it on its own.
      this.#openCartDrawer();

      // Success: reset the in-memory set and the panel.
      this.#set.clear();
      this.#renderSet();
      this.#resetCheckout();
    } catch (error) {
      document.dispatchEvent(
        new CartErrorEvent('lig-bundle-builder', 'Could not add set to cart', undefined, undefined)
      );
      this.#resetCheckout();
    }
  }

  #resetCheckout() {
    if (!(this.elCheckout instanceof HTMLButtonElement)) return;
    this.elCheckout.removeAttribute('aria-busy');
    this.#renderSet(); // restores label + disabled state from current count
  }

  /**
   * Open Horizon's cart drawer (cart-drawer-component exposes an open() method).
   * Falls back silently on themes without a drawer (full-page cart).
   */
  #openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      try {
        drawer.open();
      } catch (error) {
        /* no-op: a closed/absent drawer should never break the add */
      }
    }
  }

  /**
   * Comma-joined section ids of every cart-items-component on the page, so the
   * Section Rendering API returns fresh HTML for the drawer AND the cart page.
   * Falls back to this element's own data-section-id. (Copied from the proven
   * lig-cart-upsell / lig-pair-feature pipeline.)
   * @returns {string}
   */
  #cartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) {
        ids.add(node.dataset.sectionId);
      }
    });
    if (this.dataset.sectionId) ids.add(this.dataset.sectionId);
    return Array.from(ids).join(',');
  }

  /**
   * Fetches the current cart as a plain object (Shopify /cart.js). Returns null
   * on failure. Mirrors lig-pair-feature's #fetchCart.
   * @returns {Promise<any | null>}
   */
  async #fetchCart() {
    try {
      const cartUrl =
        typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url
          ? `${Theme.routes.cart_url}.js`
          : '/cart.js';
      const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    }
  }

  /* --- Utilities ------------------------------------------------------- */

  /**
   * Appends a Shopify CDN width param to an image URL.
   * @param {string} src
   * @param {number} width
   * @returns {string}
   */
  #withWidth(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  }

  /**
   * Returns a cents -> formatted-money function using the shop's currency.
   * @returns {(cents: number) => string}
   */
  #moneyFormatter() {
    const currency =
      (typeof window.Shopify !== 'undefined' && window.Shopify.currency && window.Shopify.currency.active) ||
      'USD';
    let intl;
    try {
      intl = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      });
    } catch {
      intl = new Intl.NumberFormat('en', { style: 'currency', currency: 'USD' });
    }
    return (cents) => {
      const value = Number(cents);
      if (!Number.isFinite(value)) return '';
      return intl.format(Math.round(value) / 100);
    };
  }
}

if (!customElements.get('lig-bundle-builder')) {
  customElements.define('lig-bundle-builder', LigBundleBuilder);
}
